import { Component, ElementRef, ViewChild } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { MagicSetIconComponent } from "./set/magic-set-icon.component";
import { NgClass, UpperCasePipe } from "@angular/common";
import { FormatNumberPipe } from "@ponder-shared/pipes/format-number.pipe";
import { Subscription, take } from "rxjs";
import { GetMagicSetsResponse } from "@apiserver-rest/ponder-data-client-base";
import { FormatDatePipe } from "@ponder-shared/pipes/format-date.pipe";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { MagicSetNode, MagicSetTree } from "./magic-set-tree";
import { EventBroker } from "@ponder-core/events/eventBroker";

@Component({
    imports: [LoadingComponent, MagicSetIconComponent, NgClass, UpperCasePipe, FormatNumberPipe, FormatDatePipe],
    templateUrl: "./magic.component.html"
})
export class MagicComponent {
    @ViewChild("setTable") public setTableRef!: ElementRef<HTMLTableElement>;

    public readonly magicSvgIcon: string = SvgIconMap.get(SvgIcon.MagicTCG)!;

    public isLoading: boolean = true;
    public loadingSuccess: boolean = false;

    public allSetNodes?: MagicSetNode[];
    public visibleNodes?: MagicSetNode[];
    public totalCards?: number;
    public visibleCount: number = 100;

    private setFilter?: string;

    private windowResizeEventSub!: Subscription;
    private windowScrollEventSub!: Subscription;

    constructor(private ponderDataClient: PonderDataClient, events: EventBroker) {
        this.windowResizeEventSub = events.ui.windowResizeEvent.subscribe({
            next: () => {
                this.recalculateVisibleCount();
            }
        });

        this.windowScrollEventSub = events.ui.windowScrollEvent.subscribe({
            next: () => {
                this.recalculateVisibleCount();
            }
        })
    }

    public ngOnDestroy(): void {
        this.windowResizeEventSub.unsubscribe();
        this.windowScrollEventSub.unsubscribe();
    }

    public ngOnInit(): void {
        this.ponderDataClient
            .magicSetAll()
            .pipe(take(1))
            .subscribe({
                next: (response: GetMagicSetsResponse) => {
                    if (response.success && response.sets?.length) {
                        this.totalCards = 0;
                        
                        response.sets.sort((a, b) => {
                            // Reverse date order so newest sets are at the top
                            var timeDiff = b.releaseDate.getTime() - a.releaseDate.getTime();

                            if (timeDiff != 0) {
                                return timeDiff;
                            }

                            var countDiff = b.cardCount! - a.cardCount!;

                            if (countDiff != 0) {
                                return countDiff;
                            }

                            return a.name!.localeCompare(b.name!);
                        });

                        this.allSetNodes = new MagicSetTree().flatten(response.sets);
                        this.allSetNodes.forEach(s => { this.totalCards! += s.set!.cardCount!; });

                        this.recalculateVisibleNodes();

                        this.loadingSuccess = true;
                    }
                }
            })
            .add(() => { this.isLoading = false; });
    }

    private recalculateVisibleCount(): void {
        if (!this.setTableRef || !this.allSetNodes?.length || this.visibleCount >= this.allSetNodes.length) {
            return;
        }

        var tableBounds = this.setTableRef.nativeElement.getBoundingClientRect();

        // .bottom is how far from the top of the visible window that the bottom is.
        // if .bottom is <= window.innerHeight, then the bottom of the table is visible.
        if (tableBounds.bottom < (window.innerHeight + 250)) {
            this.visibleCount += 250;
        }
    }

    private recalculateVisibleNodes(): void {
        this.visibleNodes = [];

        this.allSetNodes?.forEach(s => {
            if (this.isVisibleNode(s)) {
                this.visibleNodes!.push(s);
            }
        });
    }

    private isVisibleNode(node: MagicSetNode): boolean {
        if (!node.set?.cardCount || node.set!.cardCount! == 0) {
            return false;
        }

        if (!this.setFilter || this.setFilter.length == 0) {
            return true;
        } else {
            if (node.set!.name!.toLowerCase().indexOf(this.setFilter!) >= 0) {
                return true;
            }

            if (node.set!.code!.toLowerCase().indexOf(this.setFilter!) >= 0) {
                return true;
            }

            if (node.set!.block) {
                if (node.set!.block!.name!.toLowerCase().indexOf(this.setFilter!) >= 0) {
                    return true;
                }
                if (node.set!.block!.code!.toLowerCase().indexOf(this.setFilter!) >= 0) {
                    return true;
                }
            }
        }

        return false;
    }

    public onFilterChange(filter: string | undefined): void {
        this.setFilter = filter?.toLowerCase()?.trim();
        this.recalculateVisibleNodes();
    }
}